import { EventType } from '@wix/public-editor-platform-interfaces';

class EventEmitter {
  static biEvents = {
    PLATFORM_WORKER_SPAWN: 123,
    PLATFORM_APP_RUNNER_INIT_APP_API: 124,
    PLATFORM_APP_RUNNER_RUN_APP: 125,
    PLATFORM_APP_RUNNER_REMOVE_APP: 126,
    PLATFORM_WORKER_APP_BUNDLE_LOAD: 127,
    PLATFORM_WORKER_APP_BUNDLE_EXECUTE: 128,
    PLATFORM_IFRAME_INIT: 129,
    PLATFORM_APP_API_INIT: 130,
    PLATFORM_APP_API_GET: 131
  };
  static EventSteps = {
    Start: "start",
    End: "end",
    Error: "error"
  };
  uid = 0;
  callbacks = /* @__PURE__ */ new Map();
  inTransaction = false;
  isSilent = false;
  queue = [];
  silent(isSilent = true) {
    this.isSilent = isSilent;
  }
  startTransaction() {
    this.inTransaction = true;
  }
  commit() {
    this.inTransaction = false;
    const queue = this.queue;
    this.queue = [];
    if (!this.isSilent) {
      queue.forEach((event) => {
        this.notify(event);
      });
    }
  }
  notify(event) {
    if (this.isSilent) {
      return;
    }
    if (this.inTransaction) {
      this.queue.push(event);
    } else {
      this.callbacks.forEach((callback) => {
        callback(event);
      });
    }
  }
  subscribe(callback) {
    const uid = this.uid++;
    this.callbacks.set(uid, callback);
    return () => {
      this.callbacks.delete(uid);
    };
  }
  notifyWithStep(event, step) {
    this.notify({
      ...event,
      meta: {
        ...event.meta,
        step
      }
    });
  }
  async withEvent(event, cb) {
    let result = cb();
    if (result && result.hasOwnProperty("then")) {
      try {
        this.notifyWithStep(event, EventEmitter.EventSteps.Start);
        result = await result;
        this.notifyWithStep(event, EventEmitter.EventSteps.End);
        return result;
      } catch (e) {
        this.notifyWithStep(
          {
            ...event,
            meta: {
              ...event.meta,
              error: e
            }
          },
          EventEmitter.EventSteps.Error
        );
        throw e;
      }
    } else {
      this.notify(event);
      return result;
    }
  }
}

var PlatformPrivateEvent = /* @__PURE__ */ ((PlatformPrivateEvent2) => {
  PlatformPrivateEvent2["WorkerSpawn"] = "WorkerSpawn";
  PlatformPrivateEvent2["HostEvent"] = "HostEvent";
  PlatformPrivateEvent2["ApplicationsSpecsReceived"] = "ApplicationsSpecsReceived";
  PlatformPrivateEvent2["InitApplicationApi"] = "InitApplicationApi";
  PlatformPrivateEvent2["RunApplication"] = "RunApplication";
  PlatformPrivateEvent2["getApplicationApi"] = "getApplicationApi";
  PlatformPrivateEvent2["IframeInit"] = "IframeInit";
  return PlatformPrivateEvent2;
})(PlatformPrivateEvent || {});
var PlatformLifecycleEvent = /* @__PURE__ */ ((PlatformLifecycleEvent2) => {
  PlatformLifecycleEvent2["EditorReady"] = "EditorReady";
  return PlatformLifecycleEvent2;
})(PlatformLifecycleEvent || {});
class PlatformPrivateEventFactories {
  createWorkerSpawnEvent({
    url,
    workerId,
    strategy
  }) {
    return {
      type: "WorkerSpawn" /* WorkerSpawn */,
      payload: {
        url
      },
      meta: {
        bi: {
          evid: PlatformPrivateEventEmitter.biEvents.PLATFORM_WORKER_SPAWN,
          workerId,
          strategy
        }
      }
    };
  }
  createRunApplicationEvent(app) {
    return {
      type: "RunApplication" /* RunApplication */,
      payload: {
        appDefinitionId: app.appDefinitionId
      },
      meta: {
        bi: {
          appType: app.type,
          appDefId: app.appDefinitionId,
          evid: PlatformPrivateEventEmitter.biEvents.PLATFORM_APP_RUNNER_RUN_APP
        }
      }
    };
  }
  createGetApplicationApiEvent(targetAppDefId, scope) {
    return {
      type: "getApplicationApi" /* getApplicationApi */,
      payload: {},
      meta: {
        bi: {
          targetAppDefId,
          scope,
          evid: PlatformPrivateEventEmitter.biEvents.PLATFORM_APP_API_GET
        }
      }
    };
  }
  createInitApplicationApiEvent(appDefinitionId) {
    return {
      type: "InitApplicationApi" /* InitApplicationApi */,
      payload: {
        appDefinitionId
      },
      meta: {
        bi: {
          appDefId: appDefinitionId,
          evid: PlatformPrivateEventEmitter.biEvents.PLATFORM_APP_API_INIT
        }
      }
    };
  }
  createIframeInitEvent(frameUrl, iframeId) {
    return {
      type: "IframeInit" /* IframeInit */,
      payload: {},
      meta: {
        bi: {
          frameUrl,
          iframeId,
          evid: PlatformPrivateEventEmitter.biEvents.PLATFORM_IFRAME_INIT
        }
      }
    };
  }
}
class PlatformPrivateEventEmitter extends EventEmitter {
  factories = new PlatformPrivateEventFactories();
  notify(event) {
    super.notify(event);
  }
}

var PlatformAppEvent = /* @__PURE__ */ ((PlatformAppEvent2) => {
  PlatformAppEvent2["ApplicationInit"] = "ApplicationInit";
  PlatformAppEvent2["ApplicationExecute"] = "ApplicationExecute";
  PlatformAppEvent2["ApplicationRemoved"] = "ApplicationRemoved";
  PlatformAppEvent2["ApplicationLoad"] = "ApplicationLoad";
  PlatformAppEvent2["ApplicationApiInit"] = "ApplicationApiInit";
  PlatformAppEvent2["CustomEvent"] = "CustomEvent";
  PlatformAppEvent2["HostEvent"] = "HostEvent";
  PlatformAppEvent2["EditorReady"] = "EditorReady";
  return PlatformAppEvent2;
})(PlatformAppEvent || {});
class PlatformAppEventsFactories {
  createApplicationRemovedEvent(appDefinitionId) {
    return {
      type: "ApplicationRemoved" /* ApplicationRemoved */,
      payload: {
        appDefinitionId
      },
      meta: {
        bi: {
          evid: PlatformAppEventEmitter.biEvents.PLATFORM_WORKER_SPAWN,
          appDefId: appDefinitionId
        }
      }
    };
  }
  createApplicationLoadEvent(app, url) {
    return {
      type: "ApplicationLoad" /* ApplicationLoad */,
      payload: {
        appDefinitionId: app.appDefinitionId,
        url
      },
      meta: {
        bi: {
          evid: PlatformAppEventEmitter.biEvents.PLATFORM_WORKER_APP_BUNDLE_LOAD,
          appDefId: app.appDefinitionId,
          appType: app.appType,
          bundleUrl: url
        }
      }
    };
  }
  createApplicationExecuteEvent(app, url) {
    return {
      type: "ApplicationExecute" /* ApplicationExecute */,
      payload: {
        appDefinitionId: app.appDefinitionId,
        url
      },
      meta: {
        bi: {
          evid: PlatformAppEventEmitter.biEvents.PLATFORM_WORKER_APP_BUNDLE_EXECUTE,
          appDefId: app.appDefinitionId,
          appType: app.appType,
          bundleUrl: url
        }
      }
    };
  }
  createApplicationApiInitEvent(appDefinitionId, scope) {
    return {
      type: "ApplicationApiInit" /* ApplicationApiInit */,
      payload: { appDefinitionId },
      meta: {
        bi: {
          evid: PlatformAppEventEmitter.biEvents.PLATFORM_APP_API_INIT,
          appDefId: appDefinitionId,
          scope
        }
      }
    };
  }
}
class PlatformAppEventEmitter extends EventEmitter {
  factories = new PlatformAppEventsFactories();
}

class DMEventsBridge {
  constructor(events) {
    this.events = events;
  }
  static allowedEvents = [
    EventType.componentSelectionChanged,
    EventType.appInstalled,
    EventType.appUpdateCompleted,
    EventType.removeAppCompleted
  ];
  mapToPlatformHostEvent(event) {
    if (typeof event === "string") {
      try {
        event = JSON.parse(event);
      } catch (e) {
      }
    }
    return {
      type: PlatformPrivateEvent.HostEvent,
      payload: {
        type: event.args.eventType,
        ...event.args.eventPayload
      },
      meta: {
        appDefinitionId: event.appDefinitionId ?? null,
        intent: event.intent
      }
    };
  }
  /**
   * Notify by event from Worker Manager (platform infrastructure)
   */
  notify(_event) {
    const event = this.mapToPlatformHostEvent(_event);
    if (DMEventsBridge.allowedEvents.includes(event.payload.type)) {
      this.events.notify(event);
    }
  }
}

export { DMEventsBridge, EventEmitter, PlatformAppEvent, PlatformAppEventEmitter, PlatformLifecycleEvent, PlatformPrivateEvent, PlatformPrivateEventEmitter };
//# sourceMappingURL=index.mjs.map
